import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/services";

function ServiceBlock({ service, index }) {
  const reversed = index % 2 === 1;
  const Icon = service.icon;
  return (
    <section
      id={service.slug}
      className={`scroll-mt-24 py-20 lg:py-28 ${index % 2 === 1 ? "bg-empire-coal" : "bg-empire-black"}`}
      data-testid={`service-section-${service.slug}`}
    >
      <div
        className={`mx-auto flex max-w-7xl flex-col gap-12 px-6 lg:px-10 ${
          reversed ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <Reveal className="lg:w-2/5">
          <div className="relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden border border-white/10 bg-empire-black/60 p-10">
            <div className="absolute inset-0 bg-grid opacity-70" aria-hidden="true" />
            <div className="halo-red pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full" aria-hidden="true" />
            <span className="relative font-anton text-7xl text-outline-white" aria-hidden="true">
              {String(index + 1).padStart(2, "0")}
            </span>
            <div className="relative mt-10 flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center border border-empire-red/50 bg-empire-black">
                <Icon className="h-6 w-6 text-empire-red" strokeWidth={1.5} aria-hidden="true" />
              </span>
              <span className="font-grotesk text-xs uppercase tracking-[0.25em] text-empire-mist">
                Discipline {String(index + 1).padStart(2, "0")} / 07
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:w-3/5">
          <h2 className="font-anton text-3xl uppercase leading-tight sm:text-4xl" data-testid={`service-title-${service.slug}`}>
            {service.title}
          </h2>

          <h3 className="mt-8 font-grotesk text-xs uppercase tracking-[0.25em] text-empire-red">Objectives</h3>
          <p className="mt-3 text-base leading-relaxed text-empire-mist">{service.objectives}</p>

          <div className="mt-8 grid gap-10 md:grid-cols-2">
            <div>
              <h3 className="font-grotesk text-xs uppercase tracking-[0.25em] text-empire-red">
                Problems we solve
              </h3>
              <ul className="mt-4 space-y-3">
                {service.problems.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-empire-mist">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-empire-red" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-grotesk text-xs uppercase tracking-[0.25em] text-empire-red">
                What we deliver
              </h3>
              <ul className="mt-4 space-y-3">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-empire-mist">
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-empire-red" aria-hidden="true" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <h3 className="mt-10 font-grotesk text-xs uppercase tracking-[0.25em] text-empire-red">Our method</h3>
          <ol className="mt-4 grid gap-4 sm:grid-cols-2">
            {service.method.map((m, i) => (
              <li key={m.title} className="border border-white/10 p-5">
                <span className="font-anton text-2xl text-empire-red" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h4 className="mt-2 font-grotesk text-sm uppercase tracking-[0.15em] text-white">{m.title}</h4>
                <p className="mt-1 text-sm leading-relaxed text-empire-mist">{m.text}</p>
              </li>
            ))}
          </ol>

          <Link
            to={`/contact?service=${service.slug}`}
            data-testid={`service-quote-${service.slug}`}
            className="mt-10 inline-flex items-center gap-2 bg-empire-red px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
          >
            Request a Quote <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <main>
      <Seo
        title="Services — 4EMPIRE | Digital Marketing, Branding, Growth"
        description="Seven disciplines, one objective: digital marketing, brand strategy, social media, paid advertising, content, growth strategy and websites built to generate leads."
      />
      <section className="relative overflow-hidden bg-empire-black pb-16 pt-40 lg:pb-24 lg:pt-48" data-testid="services-header">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div className="halo-red pointer-events-none absolute -right-32 top-0 h-[420px] w-[420px] rounded-full" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Our services</p>
            <h1 className="mt-4 max-w-3xl font-anton text-5xl uppercase leading-[0.98] sm:text-6xl" data-testid="services-page-heading">
              Seven disciplines. <span className="text-empire-red">One objective.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-empire-mist sm:text-lg">
              Everything we do serves a single purpose: building sustainable growth for your brand.
              Explore what each service includes, the problems it solves and how we deliver it.
            </p>
          </Reveal>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <ServiceBlock key={s.slug} service={s} index={i} />
      ))}

      <section className="relative overflow-hidden bg-empire-black py-24 lg:py-32" data-testid="services-final-cta">
        <div className="halo-red pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center lg:px-10">
          <Reveal>
            <h2 className="font-anton text-4xl uppercase leading-tight sm:text-5xl">
              Not sure which <span className="text-empire-red">service fits?</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-empire-mist">
              Tell us about your project and your goals. We&apos;ll recommend the right starting
              point — no obligation.
            </p>
            <Link
              to="/contact"
              data-testid="services-final-quote-button"
              className="mt-10 inline-block bg-empire-red px-10 py-5 font-grotesk text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
            >
              Request a Quote
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
